/**
 * Zustand Auth Store - Vanilla API
 *
 * Centralized authentication state management using Zustand with Vanilla API
 * for access both inside and outside React components.
 *
 * Features:
 * - Vanilla API for non-React access
 * - localStorage persistence
 * - Type-safe actions and state
 * - OAuth flow management
 * - Permission/role checking utilities
 */

import { useEffect, useState } from "react";
import {
	createJSONStorage,
	persist,
	subscribeWithSelector,
} from "zustand/middleware";
import { createStore } from "zustand/vanilla";
import { getConfig } from "@/config";
import {
	ensureAuthenticatedI18n,
	restoreUnauthenticatedTranslations,
} from "@/i18n-authenticated";
import { queryClient } from "@/integrations/tanstack-query/root-provider";
import { getAppRouter, toRouterPath } from "@/lib/app-router";
import { authKeys, authQueryOptions, authService } from "@/services/auth";
import {
	UserInfoError,
	type UserPermissions,
} from "@/services/auth/auth.types";
import { DatadogRumService } from "@/services/datadog-rum.service";
import { MixpanelService } from "@/services/mixpanel.service";
import { getMixpanelOrgSource } from "@/stores/mixpanel-org-source";
import { isPlaceholderOrgId } from "@/utils/placeholder-org-id";
import type {
	Organization,
	Permission,
	Role,
	User,
	UserInfo,
} from "@/types/auth.types";
import { buildLoginRedirect } from "@/utils/route-guards";

// Hardcoded auth config - matches AuthProvider
const AUTH_CONFIG = {
	redirectAfterLogin: "/web/$orgId/home",
	redirectAfterLogout: "/login",
	checkAuthOnMount: true,
	authCheckInterval: 300000,
	callbackPath: "/auth/callback",
};

/**
 * Auth Store State Interface
 */
export interface AuthState {
	isAuthenticated: boolean;
	isLoading: boolean;
	isInitializing: boolean;
	isProcessingCallback: boolean;
	isPermissionsLoaded: boolean; // Track if permissions have been loaded
	user: User | null;
	error: string | null;

	// CASL Integration
	userPermissions: UserPermissions | null; // Raw permissions from API
	activeOrganizationId: string; // Currently active organization ID

	lastAuthCheck: number | null;
	callbackStartTime: number | null;
}

/**
 * Auth Store Actions Interface
 */
export interface AuthActions {
	// State setters
	setAuthState: (state: Partial<AuthState>) => void;
	setUser: (user: User | null) => void;
	setLoading: (loading: boolean) => void;
	setError: (error: string | null) => void;

	// Authentication actions
	login: (email: string, returnUrl?: string) => Promise<void>;
	/**
	 * Redirect to log-in endpoint with new orgId (no logout); uses current user email.
	 * @param options.redirectTo - Where to land after login: 'admin' | 'org-admin' | 'web'. If omitted, inferred from current path.
	 */
	redirectToLoginWithOrganization: (
		organizationId: string,
		options?: { redirectTo?: "admin" | "org-admin" | "web" },
	) => void;
	logout: () => Promise<void>;
	/**
	 * Drop the local session without calling the log-out API.
	 * Leaves `isLoading` true so the authenticated layout does not race a
	 * `/login?redirect=` navigation. The caller navigates, then `setLoading(false)`.
	 */
	clearLocalSession: () => void;
	/**
	 * Clear local auth and send the user to `/login` with redirect preserved.
	 * Used on API 401 — does not call the auth-required log-out API.
	 */
	handleUnauthorized: () => void;
	checkAuthenticationStatus: () => Promise<boolean>;
	handleOAuthReturn: () => Promise<void>;
	setCallbackProcessing: (isProcessing: boolean) => void;

	// Utility functions
	hasPermission: (permission: string) => boolean;
	hasRole: (roleName: string) => boolean;
	hasAnyPermission: (permissions: string[]) => boolean;
	hasAllPermissions: (permissions: string[]) => boolean;

	// New permissions API methods
	getCurrentUserPermissions: () => Promise<void>;
	getUserPermissions: (userId: string) => Promise<void>;
	setActiveOrganization: (organizationId: string) => void;
	checkAction: (
		actionName: string,
		entityType?: string,
		entityId?: string,
	) => Promise<boolean>;
	checkActions: (
		actions: Array<{ name: string; entityType?: string; entityId?: string }>,
	) => Promise<Map<string, boolean>>;

	// Lifecycle
	initialize: () => Promise<void>;
	reset: () => void;
	switchOrganization: (organizationId: string) => Promise<void>;
	getUserOrganizations: () => Organization[];
	can: (action: string, subject: string, field?: string) => boolean;
	cannot: (action: string, subject: string, field?: string) => boolean;
}

/**
 * Combined Auth Store Type
 */
export type AuthStore = AuthState & AuthActions;

/**
 * Initial Auth State
 */
const initialState: AuthState = {
	isAuthenticated: false,
	isLoading: false,
	isInitializing: true,
	isProcessingCallback: false,
	isPermissionsLoaded: false,
	user: null,
	error: null,

	// CASL Integration
	userPermissions: null,
	activeOrganizationId: "",
	// activeOrganization: null,

	lastAuthCheck: null,
	callbackStartTime: null,
};

/**
 * Persisted state type - only these fields are saved to localStorage
 */
type PersistedAuthState = Pick<
	AuthState,
	"isAuthenticated" | "user" | "lastAuthCheck" | "activeOrganizationId"
>;

/** Prevents parallel 401s from stacking login redirects. */
let isHandlingUnauthorized = false;

/**
 * Identify the signed-in user to Datadog RUM (or clear it on sign-out).
 *
 * `displayName` can be null from the API; fall back to "First Last" so the
 * session does not show up as an anonymous "Public User" in Datadog.
 */
export function syncUserToDatadogRum(user: User | null): void {
	if (!user) {
		DatadogRumService.removeUser();
		return;
	}

	const name =
		user.displayName?.trim() ||
		`${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();

	DatadogRumService.addUser(user.id, user.email, name || undefined, {
		roles: user.roles?.map((r) => r.name).join(", "),
		permissions: user.permissions?.length || 0,
		organizations: user.organizations?.length || 0,
	});
}

/**
 * Re-send the current auth-store user to Datadog RUM.
 * Call after RUM finishes initializing: `addUser` is a no-op before init, and
 * auth usually resolves before the runtime config does.
 */
export function syncCurrentUserToDatadogRum(): void {
	const { user } = authStore.getState();
	if (user) {
		syncUserToDatadogRum(user);
	}
}

function trimmedOrganizationName(
	name: string | null | undefined,
): string | undefined {
	const trimmed = name?.trim();
	return trimmed || undefined;
}

function organizationNameFor(
	user: User | null,
	organizationId: string,
): string | undefined {
	if (isPlaceholderOrgId(organizationId)) return undefined;
	const fromUser = trimmedOrganizationName(
		user?.organizations?.find(
			(organization) => organization.id === organizationId,
		)?.name,
	);
	if (fromUser) return fromUser;
	const fromPermissions = trimmedOrganizationName(
		authStore
			.getState()
			.userPermissions?.organizations.find(
				(organization) => organization.id === organizationId,
			)?.name,
	);
	if (fromPermissions) return fromPermissions;
	return trimmedOrganizationName(
		getMixpanelOrgSource().availableOrganizations.find(
			(organization) => organization.id === organizationId,
		)?.name,
	);
}

/**
 * Platform admin with All Organizations selected has no concrete organization.
 * Events in that mode carry neither an organization id nor a name.
 */
function mixpanelOrganizationIsUnscoped(): boolean {
	const { currentApp, currentOrganization } = getMixpanelOrgSource();
	return (
		(currentApp === "admin" || currentApp === "org-admin") &&
		isPlaceholderOrgId(currentOrganization?.id)
	);
}

function syncMixpanelOrganization(
	organizationId: string,
	user: User | null,
): void {
	if (
		mixpanelOrganizationIsUnscoped() ||
		isPlaceholderOrgId(organizationId)
	) {
		MixpanelService.clearOrganization();
		return;
	}
	MixpanelService.setOrganization(
		organizationId,
		organizationNameFor(user, organizationId),
	);
}

/**
 * Identify the signed-in user to Mixpanel, including the active organization
 * when its id (and name, if already loaded) are known.
 */
export function syncUserToMixpanel(user: User | null): void {
	if (!user) {
		MixpanelService.reset();
		return;
	}

	MixpanelService.identify(user.id, user.email);
	syncMixpanelOrganization(
		authStore.getState().activeOrganizationId,
		user,
	);
}

/** Re-send the current auth-store user after Mixpanel finishes initializing. */
export function syncCurrentUserToMixpanel(): void {
	const { user } = authStore.getState();
	if (user) {
		syncUserToMixpanel(user);
	}
}

/**
 * Shared local-session teardown for logout and 401 handling.
 * `isLoading: true` on intentional logout blocks `_authenticated` from racing
 * to `/login` before the API/IdP document navigation runs.
 */
function clearLocalAuthSession(
	setAuthState: AuthActions["setAuthState"],
	isLoading: boolean,
): void {
	if (typeof window !== "undefined") {
		sessionStorage.removeItem("auth_redirect_url");
	}

	setAuthState({
		isAuthenticated: false,
		isLoading,
		isInitializing: false,
		isPermissionsLoaded: false,
		user: null,
		error: null,
		lastAuthCheck: Date.now(),
		activeOrganizationId: "",
		userPermissions: null,
	});

	authStore.persist.clearStorage();
	restoreUnauthenticatedTranslations();
	DatadogRumService.removeUser();
	MixpanelService.reset();
}

/**
 * Create Zustand Auth Store with Vanilla API and Persist Middleware
 */
export const authStore = createStore<AuthStore>()(
	subscribeWithSelector(
		persist(
			(set, get) => ({
				// Initial state
				...initialState,

				// State setters
				setAuthState: (newState: Partial<AuthState>) => {
					set((state) => ({ ...state, ...newState }));
				},

				setActiveOrganization: (organizationId: string) => {
					set((state) => ({ ...state, activeOrganizationId: organizationId }));
					syncMixpanelOrganization(organizationId, get().user);
				},

				setUser: (user: User | null) => {
					set((state) => ({
						...state,
						user,
						isAuthenticated: !!user,
					}));

					syncUserToDatadogRum(user);
					syncUserToMixpanel(user);
				},

				setLoading: (isLoading: boolean) => {
					set((state) => ({ ...state, isLoading }));
				},

				setError: (error: string | null) => {
					set((state) => ({ ...state, error }));
				},

				setCallbackProcessing: (isProcessingCallback: boolean) => {
					set((state) => ({
						...state,
						isProcessingCallback,
						callbackStartTime: isProcessingCallback ? Date.now() : null,
					}));
				},

				// Authentication actions
				login: async (email: string, returnUrl?: string) => {
					const { setLoading, setError } = get();

					try {
						setLoading(true);
						setError(null);

						const config = getConfig();
						const redirectUrl = returnUrl || AUTH_CONFIG.redirectAfterLogin;

						if (typeof window !== "undefined") {
							sessionStorage.setItem("auth_redirect_url", redirectUrl);
						}

						const callbackUrl = `${window.location.origin}${AUTH_CONFIG.callbackPath}`;
						const oauthUrl = `${config?.api.baseUrl}/authentication/log-in?email=${encodeURIComponent(email)}&returnUrl=${encodeURIComponent(callbackUrl)}&tzOffset=${new Date().getTimezoneOffset()}`;

						window.location.href = oauthUrl;
					} catch (error) {
						console.error("❌ Zustand: Login failed:", error);
						setLoading(false);
						setError(error instanceof Error ? error.message : "Login failed");
					}
				},

				redirectToLoginWithOrganization: (
					organizationId: string,
					options?: { redirectTo?: "admin" | "org-admin" | "web" },
				) => {
					const pathname =
						typeof window !== "undefined" ? window.location.pathname : "";
					const destination =
						options?.redirectTo ??
						(pathname.startsWith("/org-admin")
							? "org-admin"
							: pathname.startsWith("/admin")
								? "admin"
								: "web");

					// Admin or "all" org: no login needed, just redirect
					if (destination === "admin" || organizationId === "all") {
						if (typeof window !== "undefined") {
							window.location.href = "/admin/home";
						}
						return;
					}

					const { user } = get();
					const email = user?.email;
					if (!email) {
						window.location.href = `/login?organizationId=${encodeURIComponent(organizationId)}`;
						return;
					}
					const config = getConfig();
					const redirectUrl =
						destination === "org-admin"
							? `/org-admin/${organizationId}/home`
							: AUTH_CONFIG.redirectAfterLogin;
					if (typeof window !== "undefined") {
						sessionStorage.setItem("auth_redirect_url", redirectUrl);
					}
					const callbackUrl = `${window.location.origin}${AUTH_CONFIG.callbackPath}`;
					const oauthUrl = `${config?.api.baseUrl}/authentication/log-in?email=${encodeURIComponent(email)}&returnUrl=${encodeURIComponent(callbackUrl)}&tzOffset=${new Date().getTimezoneOffset()}&organizationId=${encodeURIComponent(organizationId)}`;

					window.location.href = oauthUrl;
				},

				clearLocalSession: () => {
					clearLocalAuthSession(get().setAuthState, true);
				},

				logout: async () => {
					const { setAuthState, setLoading } = get();

					try {
						setLoading(true);

						// Keep isLoading true so _authenticated does not client-navigate
						// to /login before the API/IdP document navigation runs.
						clearLocalAuthSession(setAuthState, true);

						await authService.logOut();
					} catch (error) {
						console.error("❌ Zustand: Logout failed:", error);
						setLoading(false);
						get().setError(
							error instanceof Error ? error.message : "Logout failed",
						);
					}
				},

				handleUnauthorized: () => {
					if (typeof window === "undefined") {
						return;
					}
					if (isHandlingUnauthorized) {
						return;
					}

					const currentPath = window.location.pathname;
					if (
						currentPath === "/login" ||
						currentPath === "/signed-out" ||
						currentPath === "/auth/callback"
					) {
						return;
					}

					isHandlingUnauthorized = true;
					try {
						const { setAuthState } = get();
						clearLocalAuthSession(setAuthState, false);

						const redirectTarget = buildLoginRedirect({
							pathname: window.location.pathname,
							searchStr: window.location.search,
							hash: window.location.hash,
						});
						const loginPath = `/login?redirect=${encodeURIComponent(redirectTarget)}`;
						const router = getAppRouter();
						if (router) {
							void router.navigate({ to: loginPath, replace: true });
						} else {
							window.location.assign(loginPath);
						}
					} finally {
						isHandlingUnauthorized = false;
					}
				},

				checkAuthenticationStatus: async () => {
					const { setAuthState, setLoading } = get();
					const currentState = get();

					try {
						// Prevent multiple rapid calls - debounce with 1 second
						const now = Date.now();
						const timeSinceLastCheck = currentState.lastAuthCheck
							? now - currentState.lastAuthCheck
							: Number.POSITIVE_INFINITY;

						if (timeSinceLastCheck < 1000) {
							console.log(
								"⏳ Zustand: Skipping auth check - too soon since last check",
							);
							return currentState.isAuthenticated;
						}

						console.log(
							"🔍 Zustand: Checking authentication status and permissions...",
						);
						setLoading(true);

						// Fetch both userInfo and permissions in parallel using query options
						// Use ensureQueryData to keep data in cache even without active observers
						const [userInfo, userPermissions] = await Promise.all([
							queryClient.ensureQueryData(authQueryOptions.userInfo()),
							queryClient.ensureQueryData(authQueryOptions.permissions()),
						]);

						// Transform userInfo to User format
						const baseUser: UserInfo = userInfo;

						// Transform flattened permissions to match internal Permission type
						const transformedPermissions = (
							userPermissions.permissions || []
						).map((p: any) => ({
							id: p.id,
							name: p.name,
							action: p.name, // Permission name becomes action
							resource: p.organizationId, // Organization ID as resource
							conditions: {
								organizationId: p.organizationId,
								organizationName: p.organizationName,
							},
						}));

						// Transform platform roles and organization roles
						const platformRoles = (userPermissions.platformRoles || []).map(
							(role: any) => ({
								id: role.code.toString(),
								name: role.name,
								description: role.description,
								permissions: [], // Platform roles don't have embedded permissions in this context
							}),
						);

						const organizationRoles = (userPermissions.organizations || []).map(
							(org: any) => ({
								id: org.id,
								name: `${org.name} Member`,
								description: `Member of ${org.name}`,
								organizationId: org.id,
								permissions: [], // Roles don't have embedded permissions in this context
							}),
						);

						const transformedRoles = [...platformRoles, ...organizationRoles];

						// Combine user data with permissions
						const user: any = {
							...baseUser,
							permissions: transformedPermissions,
							roles: transformedRoles,
							organizations: userPermissions.organizations || [],
						};

						const activeOrganizationId =
							// Use || (not ??) so that empty-string values fall through to the
							// next fallback instead of short-circuiting the chain.
							(userInfo as { currentOrganizationId?: string | null })
								.currentOrganizationId ||
							currentState.activeOrganizationId ||
							userPermissions.organizations?.find(
								(o: UserPermissions["organizations"][number]) => o.active,
							)?.id ||
							userPermissions.organizations?.[0]?.id ||
							"";

						// Apply API localizations before flipping auth state so OAuth callback
						// navigation into `/_authenticated` does not flash bootstrap English
						// on the pending spinner.
						try {
							await ensureAuthenticatedI18n(activeOrganizationId || undefined);
						} catch (i18nError) {
							console.error(
								"❌ Zustand: Failed to apply authenticated localizations:",
								i18nError,
							);
						}

						setAuthState({
							isAuthenticated: true,
							isLoading: false,
							isInitializing: false,
							isPermissionsLoaded: true, // Mark permissions as loaded
							user,
							error: null,
							lastAuthCheck: Date.now(),
							activeOrganizationId,
						});
						syncUserToDatadogRum(user);
						syncUserToMixpanel(user);

						console.log(
							"✅ Zustand: Authentication and permissions loaded successfully",
						);
						console.log(
							`📋 Zustand: Loaded ${transformedPermissions.length} permissions and ${transformedRoles.length} roles`,
						);
						return true;
					} catch (error) {
						const isUnauthorized =
							error instanceof UserInfoError && error.statusCode === 401;

						console.log(
							`❌ Zustand: Authentication failed: ${isUnauthorized ? "Unauthorized" : (error as Error).message}`,
						);

						// Only clear localStorage if user wasn't already authenticated
						const wasAlreadyAuthenticated = currentState.isAuthenticated;
						if (!wasAlreadyAuthenticated) {
							setAuthState({
								isAuthenticated: false,
								isLoading: false,
								isInitializing: false,
								isPermissionsLoaded: false,
								user: null,
								error: isUnauthorized ? null : (error as Error).message,
								lastAuthCheck: Date.now(),
							});
						} else {
							console.log(
								"⚠️ Zustand: API check failed but user was already authenticated - keeping state",
							);
							setLoading(false);
						}

						return false;
					}
				},

				handleOAuthReturn: async () => {
					const { checkAuthenticationStatus, setError, setCallbackProcessing } =
						get();
					const currentState = get();

					try {
						console.log("🔄 Zustand: Processing OAuth return...");

						// Set callback processing state immediately
						setCallbackProcessing(true);

						// Flush any stale or errored auth query cache entries (e.g. from a
						// previous unauthenticated check on the protected route before login).
						queryClient.removeQueries({ queryKey: authKeys.all });

						// Delay the actual auth check slightly so the backend session
						// has time to propagate after the OAuth redirect, then keep
						// the loading indicator visible for ~1.5s total.
						const minLoadingTime = 1300;
						await new Promise((resolve) => setTimeout(resolve, 200));

						// Run authentication check
						const authPromise = checkAuthenticationStatus();

						// Wait for both auth check and minimum time
						await Promise.all([
							authPromise,
							new Promise((resolve) => setTimeout(resolve, minLoadingTime)),
						]);

						// Clear callback processing state
						setCallbackProcessing(false);

						console.log("✅ Zustand: OAuth return processed successfully");
						// Don't return authResult to match Promise<void> signature
					} catch (error) {
						console.error("❌ Zustand: OAuth return handling failed:", error);

						// Ensure we've shown loading for at least minimum time before showing error
						const elapsed =
							Date.now() - (currentState.callbackStartTime || Date.now());
						const remainingTime = Math.max(0, 1500 - elapsed);

						if (remainingTime > 0) {
							await new Promise((resolve) =>
								setTimeout(resolve, remainingTime),
							);
						}

						setCallbackProcessing(false);
						setError(
							error instanceof Error ? error.message : "Authentication failed",
						);
					}
				},

				// Utility functions
				hasPermission: (permissionName: string): boolean => {
					const { isAuthenticated, user } = get();
					if (!isAuthenticated || !user || !user.roles) return false;

					const permissions = user.roles.flatMap((role) => role.permissions);
					return permissions.some(
						(permission) => permission.name === permissionName,
					);
				},

				hasRole: (roleName: string): boolean => {
					const { isAuthenticated, user } = get();
					if (!isAuthenticated || !user || !user.roles) return false;

					return user.roles.some((role) => role.name === roleName);
				},

				hasAnyPermission: (permissions: string[]): boolean => {
					const { hasPermission } = get();
					return permissions.some((permission) => hasPermission(permission));
				},

				hasAllPermissions: (permissions: string[]): boolean => {
					const { hasPermission } = get();
					return permissions.every((permission) => hasPermission(permission));
				},

				// =============================================================================
				// NEW PERMISSIONS API METHODS - Swagger Integration
				// =============================================================================

				/**
				 * Fetch current user's permissions from API and update store
				 */
				getCurrentUserPermissions: async (): Promise<void> => {
					const { setLoading, setError, setUser } = get();
					const currentState = get();

					try {
						setLoading(true);
						setError(null);

						console.log("🔍 Zustand: Fetching current user permissions...");
						const userPermissions = await queryClient.ensureQueryData(
							authQueryOptions.permissions(),
						);

						// Store raw UserPermissions for CASL
						set((state) => ({
							...state,
							userPermissions,
							isPermissionsLoaded: true,
						}));

						// Update user with permissions data (legacy format for backward compatibility)
						if (currentState.user) {
							// Transform flattened permissions to match internal Permission type
							const transformedPermissions = (
								userPermissions.permissions || []
							).map((p: any) => ({
								id: p.id,
								name: p.name,
								action: p.name, // Permission name becomes action
								resource: p.organizationId, // Organization ID as resource
								conditions: {
									organizationId: p.organizationId,
									organizationName: p.organizationName,
								},
							}));

							// Transform platform roles and organization roles
							const platformRoles = (userPermissions.platformRoles || []).map(
								(role: any) => ({
									id: role.code.toString(),
									name: role.name,
									description: role.description,
									permissions: [], // Platform roles don't have embedded permissions in this context
								}),
							);

							const organizationRoles = (
								userPermissions.organizations || []
							).map((org: any) => ({
								id: org.id,
								name: `${org.name} Member`,
								description: `Member of ${org.name}`,
								organizationId: org.id,
								permissions: [], // Roles don't have embedded permissions in this context
							}));

							const transformedRoles = [...platformRoles, ...organizationRoles];

							const updatedUser: User = {
								...currentState.user,
								permissions: transformedPermissions as Permission[],
								roles: transformedRoles as Role[],
								organizations: userPermissions.organizations as Organization[],
							};
							setUser(updatedUser);

							// Set active organization if user has organizations and none is set
							if (
								!currentState.activeOrganizationId &&
								userPermissions.organizations?.length > 0
							) {
								const firstActiveOrg = userPermissions.organizations.find(
									(org) => org.active,
								);
								if (firstActiveOrg) {
									get().setActiveOrganization(firstActiveOrg.id);
								}
							}
						}

						console.log("✅ Zustand: Current user permissions updated", {
							permissionsCount: userPermissions.permissions?.length || 0,
							platformRolesCount: userPermissions.platformRoles?.length || 0,
							organizationsCount: userPermissions.organizations?.length || 0,
						});
					} catch (error) {
						console.error(
							"❌ Zustand: Failed to fetch current user permissions:",
							error,
						);
						setError(
							error instanceof Error
								? error.message
								: "Failed to fetch permissions",
						);
					} finally {
						setLoading(false);
					}
				},

				/**
				 * Fetch specific user's permissions from API
				 */
				getUserPermissions: async (userId: string): Promise<void> => {
					const { setLoading, setError } = get();

					try {
						setLoading(true);
						setError(null);

						console.log(
							`🔍 Zustand: Fetching permissions for user ${userId}...`,
						);
						const userPermissions = await queryClient.fetchQuery(
							authQueryOptions.userPermissions(userId),
						);

						// Note: This doesn't update the current user - just fetches data
						// In a real app, you might want to store this in a separate state
						console.log(
							"✅ Zustand: User permissions fetched:",
							userPermissions,
						);
					} catch (error) {
						console.error(
							`❌ Zustand: Failed to fetch permissions for user ${userId}:`,
							error,
						);
						setError(
							error instanceof Error
								? error.message
								: "Failed to fetch user permissions",
						);
					} finally {
						setLoading(false);
					}
				},

				/**
				 * Check if current user can perform a specific action
				 */
				checkAction: async (
					actionName: string,
					entityType?: string,
					entityId?: string,
				): Promise<boolean> => {
					try {
						console.log(
							`🔍 Zustand: Checking action permission: ${actionName}`,
						);
						const result = await authService.checkAction({
							name: actionName,
							entityType: entityType || null,
							entityId: entityId || null,
						});

						console.log(`✅ Zustand: Action ${actionName} permission:`, result);
						return result;
					} catch (error) {
						console.error(
							`❌ Zustand: Failed to check action ${actionName}:`,
							error,
						);
						return false; // Fail safe - deny permission on error
					}
				},

				/**
				 * Check multiple actions at once
				 */
				checkActions: async (
					actions: Array<{
						name: string;
						entityType?: string;
						entityId?: string;
					}>,
				): Promise<Map<string, boolean>> => {
					try {
						console.log(
							"🔍 Zustand: Checking multiple action permissions:",
							actions.map((a) => a.name),
						);

						const actionsToCheck = actions.map((action) => ({
							name: action.name,
							entityType: action.entityType || null,
							entityId: action.entityId || null,
						}));

						const result = await authService.checkActions(actionsToCheck);

						console.log("✅ Zustand: Multiple actions permissions:", result);
						return result;
					} catch (error) {
						console.error(
							"❌ Zustand: Failed to check multiple actions:",
							error,
						);
						return new Map(); // Fail safe - return empty map on error
					}
				},

				// Lifecycle methods
				initialize: async () => {
					const { setAuthState, checkAuthenticationStatus } = get();

					try {
						// Wait for hydration if not already hydrated
						if (!authStore.persist.hasHydrated()) {
							console.log("⏳ Zustand: Waiting for hydration...");
							await authStore.persist.rehydrate();
						}

						// Don't run auth check if we're on the callback route
						if (
							typeof window !== "undefined" &&
							window.location.pathname === AUTH_CONFIG.callbackPath
						) {
							setAuthState({
								isInitializing: false,
							});
							return;
						}

						if (AUTH_CONFIG.checkAuthOnMount) {
							// Always verify the session on mount. This re-populates activeOrganizationId,
							// userInfo cache, and isPermissionsLoaded so that route guards and
							// field-info localization requests have a valid org ID. For unauthenticated
							// users the 401 is harmless — the interceptor guard (isCurrentlyAuthenticated)
							// prevents a spurious logout(), and checkAuthenticationStatus()'s catch block
							// clears the auth state cleanly.
							await checkAuthenticationStatus();
						} else {
							setAuthState({
								isInitializing: false,
							});
						}
					} catch (error) {
						console.error(
							"❌ Zustand: Failed to initialize authentication:",
							error,
						);
						setAuthState({
							isAuthenticated: false,
							isLoading: false,
							isInitializing: false,
							user: null,
							error:
								error instanceof Error
									? error.message
									: "Authentication initialization failed",
							lastAuthCheck: Date.now(),
						});
					}
				},

				reset: () => {
					set(initialState);
					// Clear persisted storage using persist API
					authStore.persist.clearStorage();
				},
				switchOrganization: async (organizationId: string) => {
					const currentState = get();

					// Find the organization in user's available organizations
					const targetOrganization =
						currentState.userPermissions?.organizations?.find(
							(org) => org.id === organizationId,
						);

					console.log("targetOrganization", targetOrganization);

					if (!targetOrganization) {
						console.error(
							"❌ Zustand: Cannot switch to organization - not found or no access",
							organizationId,
						);
						return;
					}

					// Update active organization
					set((state) => ({
						...state,
						activeOrganization: targetOrganization as Organization,
					}));

					// Update the user's lastActiveOrganizationId if user exists
					if (currentState.user) {
						const updatedUser = {
							...currentState.user,
							lastActiveOrganizationId: organizationId,
						};

						set((state) => ({
							...state,
							user: updatedUser,
						}));
					}

					console.log("✅ Zustand: Switched to organization", {
						organizationId,
						organizationName: targetOrganization.name,
					});

					// TODO: Make API call to persist the organization switch
					// This would typically call an endpoint like PUT /user/active-organization
					// await authService.switchActiveOrganization(organizationId);
				},
				getUserOrganizations: () => {
					return get().user?.organizations || [];
				},
				can: (action: string, subject: string, _field?: string) => {
					return (
						get().user?.permissions?.some(
							(permission) =>
								permission.action === action &&
								permission?.resource === subject &&
								permission?.action === action,
						) || false
					);
				},
				cannot: (action: string, subject: string, _field?: string) => {
					return (
						!get().user?.permissions?.some(
							(permission) =>
								permission.action === action &&
								permission?.resource === subject &&
								permission?.action === action,
						) || false
					);
				},
			}),
			{
				name: "auth_state_2", // localStorage key (keep same for backward compatibility)
				storage: createJSONStorage(() => localStorage),
				version: 1, // Enables future migrations
				partialize: (state): PersistedAuthState => ({
					isAuthenticated: state.isAuthenticated,
					user: state.user,
					lastAuthCheck: state.lastAuthCheck,
					activeOrganizationId: state.activeOrganizationId,
				}),
				onRehydrateStorage: () => {
					// console.log("📖 Zustand: Hydration starting...");
					return (state, error) => {
						if (error) {
							console.error("❌ Zustand: Hydration failed:", error);
						} else if (state?.user) {
							syncUserToDatadogRum(state.user);
							syncUserToMixpanel(state.user);
						}
					};
				},
			},
		),
	),
);

/**
 * Navigation handler for OAuth callback
 * Watches for authentication state changes and redirects from callback
 */
authStore.subscribe(
	(state) => ({
		isAuthenticated: state.isAuthenticated,
		isLoading: state.isLoading,
		isInitializing: state.isInitializing,
		isPermissionsLoaded: state.isPermissionsLoaded,
		user: state.user,
		activeOrganizationId: state.activeOrganizationId,
	}),
	(current, _previous) => {
		// Only handle navigation if we're on the callback route and auth + permissions are fully loaded
		if (
			current.isAuthenticated &&
			!current.isLoading &&
			!current.isInitializing &&
			current.isPermissionsLoaded &&
			current.user !== null &&
			typeof window !== "undefined" &&
			window.location.pathname === AUTH_CONFIG.callbackPath
		) {
			// Get stored redirect URL — sanitize to prevent login-loop URLs from a prior
			// redirect-encoding bug where the value pointed back at /login instead of the app.
			const rawRedirectUrl =
				sessionStorage.getItem("auth_redirect_url") ||
				AUTH_CONFIG.redirectAfterLogin;
			const redirectUrlIsLoginLoop =
				rawRedirectUrl.includes("/login") ||
				rawRedirectUrl === AUTH_CONFIG.redirectAfterLogin.replace("$orgId", "");
			const redirectUrl = redirectUrlIsLoginLoop
				? AUTH_CONFIG.redirectAfterLogin
				: rawRedirectUrl;

			// Clean up
			sessionStorage.removeItem("auth_redirect_url");

			// Resolve $orgId placeholder with actual active organization; never leave empty (avoids /web/home)
			const userInfo = queryClient.getQueryData<{
				organizationUserInfo?: Array<{ organization?: { id: string } }>;
			}>(authQueryOptions.userInfo().queryKey);
			const orgIdForRedirect =
				current.activeOrganizationId ||
				userInfo?.organizationUserInfo?.[0]?.organization?.id ||
				"";
			const resolvedUrl = redirectUrl.replace(/\$orgId/g, orgIdForRedirect);

			console.log(
				"✅ Zustand: Authentication and permissions FULLY confirmed, redirecting to:",
				resolvedUrl,
			);
			console.log("✅ Zustand: Auth state:", {
				isAuthenticated: current.isAuthenticated,
				isLoading: current.isLoading,
				isInitializing: current.isInitializing,
				isPermissionsLoaded: current.isPermissionsLoaded,
				activeOrganizationId: current.activeOrganizationId || "",
				hasUser: !!current.user,
			});

			// Client-side navigate so authenticated i18n applied during
			// checkAuthenticationStatus survives into `/_authenticated` pending UI.
			const path = toRouterPath(resolvedUrl);
			const router = getAppRouter();
			console.log("🚀 Zustand: Client navigating after OAuth to:", path);
			if (router) {
				void router.navigate({ to: path, replace: true });
			} else {
				window.location.href = resolvedUrl;
			}
		}
	},
);

/**
 * Synchronous auth state getter for non-React contexts
 */
export const getAuthState = () => authStore.getState();

/**
 * Check if user is authenticated (synchronous)
 */
export const isAuthenticated = () => authStore.getState().isAuthenticated;

/**
 * Get current user (synchronous)
 */
export const getCurrentUser = () => authStore.getState().user;

/**
 * Subscribe to auth state changes
 */
export const subscribeToAuth = (callback: (state: AuthState) => void) => {
	return authStore.subscribe(callback);
};

/**
 * Initialize the auth store (call once on app startup)
 */
export const initializeAuthStore = async () => {
	await authStore.getState().initialize();
};

/**
 * React hook to check if the auth store has been hydrated from localStorage
 * Useful for preventing flash of unauthenticated content
 */
export const useAuthHydration = () => {
	const [hydrated, setHydrated] = useState(authStore.persist.hasHydrated());

	useEffect(() => {
		// Subscribe to hydration events
		const unsubFinishHydration = authStore.persist.onFinishHydration(() =>
			setHydrated(true),
		);

		// Check current hydration status (in case it already finished)
		setHydrated(authStore.persist.hasHydrated());

		return () => {
			unsubFinishHydration();
		};
	}, []);

	return hydrated;
};

/**
 * Check if auth store has been hydrated (synchronous, non-React)
 */
export const hasAuthHydrated = () => authStore.persist.hasHydrated();

// Export default store for convenience
export default authStore;
